import { c as _c } from "react/compiler-runtime";
import React, { useId } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { RangeFilterProps } from '@/types/searchSide';
interface QuickRangeFilterProps extends Omit<RangeFilterProps, 'step'> {
  limits: readonly number[];
  formatLimit: (value: number) => string;
  anyLabel: string;
}
export const QuickRangeFilter: React.FC<QuickRangeFilterProps> = t0 => {
  const $ = _c(38);
  const {
    title,
    min,
    max,
    value,
    onChange,
    onClear,
    isOpen,
    onToggle,
    limits,
    formatLimit,
    anyLabel,
    formatValue: t1,
    className: t2,
    "aria-label": ariaLabel
  } = t0;
  const formatValue = t1 === undefined ? String : t1;
  const className = t2 === undefined ? "" : t2;
  const id = useId();
  const isAny = value[0] === min && value[1] === max;
  const selectedLimit = !isAny && value[0] === 0 && limits.includes(value[1]) ? value[1] : undefined;
  let t3;
  if ($[0] !== formatLimit || $[1] !== formatValue || $[2] !== selectedLimit || $[3] !== value[0] || $[4] !== value[1]) {
    t3 = selectedLimit !== undefined ? `Up to ${formatLimit(selectedLimit)}` : `${formatValue(value[0])} – ${formatValue(value[1])}`;
    $[0] = formatLimit;
    $[1] = formatValue;
    $[2] = selectedLimit;
    $[3] = value[0];
    $[4] = value[1];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const summary = t3;
  const buttonClass = _temp;
  const t4 = `${id}-trigger`;
  const t5 = `${id}-options`;
  let t6;
  if ($[6] !== title) {
    t6 = <span>{title}</span>;
    $[6] = title;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isOpen) {
    t7 = isOpen ? <ChevronUp className="t-h-4 t-w-4" aria-hidden="true" /> : <ChevronDown className="t-h-4 t-w-4" aria-hidden="true" />;
    $[8] = isOpen;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== isOpen || $[11] !== onToggle || $[12] !== t4 || $[13] !== t5 || $[14] !== t6 || $[15] !== t7) {
    t8 = <button type="button" id={t4} aria-expanded={isOpen} aria-controls={t5} onClick={onToggle} className="t-flex t-w-full t-items-center t-justify-between t-text-left t-text-sm t-font-extrabold t-text-[#38323d] hover:t-text-brand-violet focus-visible:t-outline-none focus-visible:t-ring-2 focus-visible:t-ring-brand-violet focus-visible:t-ring-offset-2">{t6}{t7}</button>;
    $[10] = isOpen;
    $[11] = onToggle;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== isAny || $[18] !== onClear || $[19] !== summary || $[20] !== title) {
    t9 = !isAny && <div className="t-mt-2 t-flex t-items-center t-justify-between t-gap-2 t-text-xs"><span className="t-text-brand-ink-muted">{summary}</span><button type="button" onClick={onClear} aria-label={`Clear ${title.toLowerCase()} filter`} className="t-min-h-11 t-px-2 t-font-semibold t-text-brand-violet t-underline focus-visible:t-outline-none focus-visible:t-ring-2 focus-visible:t-ring-brand-violet">Clear</button></div>;
    $[17] = isAny;
    $[18] = onClear;
    $[19] = summary;
    $[20] = title;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== anyLabel || $[23] !== formatLimit || $[24] !== id || $[25] !== isAny || $[26] !== isOpen || $[27] !== limits || $[28] !== onChange || $[29] !== onClear || $[30] !== selectedLimit) {
    t10 = isOpen && <div id={`${id}-options`} role="group" aria-labelledby={`${id}-trigger`} className="t-mt-4 t-grid t-grid-cols-2 t-gap-2"><button type="button" aria-pressed={isAny} onClick={onClear} className={buttonClass(isAny)}>{anyLabel}</button>{limits.map(limit => <button key={limit} type="button" aria-pressed={selectedLimit === limit} onClick={() => onChange([0, limit])} className={buttonClass(selectedLimit === limit)}>Up to {formatLimit(limit)}</button>)}</div>;
    $[22] = anyLabel;
    $[23] = formatLimit;
    $[24] = id;
    $[25] = isAny;
    $[26] = isOpen;
    $[27] = limits;
    $[28] = onChange;
    $[29] = onClear;
    $[30] = selectedLimit;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== ariaLabel || $[33] !== className || $[34] !== t10 || $[35] !== t8 || $[36] !== t9) {
    t11 = <div className={className} aria-label={ariaLabel}>{t8}{t9}{t10}</div>;
    $[32] = ariaLabel;
    $[33] = className;
    $[34] = t10;
    $[35] = t8;
    $[36] = t9;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  return t11;
};
function _temp(selected) {
  return `t-min-h-11 t-rounded-none t-border t-px-2 t-py-2 t-text-sm t-font-semibold t-transition-colors focus-visible:t-outline-none focus-visible:t-ring-2 focus-visible:t-ring-brand-violet focus-visible:t-ring-offset-2 ${selected ? "t-border-brand-violet t-bg-brand-violet t-text-white" : "t-border-gray-300 t-bg-white t-text-brand-ink hover:t-border-brand-violet hover:t-bg-brand-lavender/30"}`;
}